import * as Sentry from '@sentry/react';
import firebase from 'utils/firebase';

interface FirestoreErrorLike {
  code?: string;
  message?: string;
}

// One report per code+path per page load: terminal listener errors repeat on
// every remount and must not flood the Sentry quota.
const reportedToSentry = new Set<string>();

// First-user gate for the suppression below: before the first user of this
// page load was seen, a permission-denied is startup noise. After a session
// existed it can be real signal (e.g. a user-triggered write failing after
// token revocation) and must report.
let hadUser = false;
firebase.auth().onAuthStateChanged((user) => {
  if (user) hadUser = true;
});

/**
 * Logs a Firestore error together with the document, collection or query it
 * hit and reports it to Sentry under that path. The raw SDK message ("Missing
 * or insufficient permissions.") is useless on its own because it names
 * neither the query nor the caller.
 */
function logFirestoreError(source: string, path: string, error: unknown) {
  const { code = 'unknown', message = String(error) } =
    (error as FirestoreErrorLike | null) ?? {};

  // Two expected-noise classes of permission-denied, neither is a rights bug:
  // page-load listeners that attached before auth had restored the session
  // (the rules saw request.auth == null and denied every collection into
  // Sentry), and paths built from a not-yet-resolved id ("restaurants/null/…")
  // that no rule can ever answer; the caller re-runs once the id arrives.
  if (code === 'permission-denied') {
    if (!firebase.auth().currentUser && !hadUser) return;
    if (/(^|\/)(null|undefined)(\/|$)/.test(path)) return;
  }

  const description = `${code} at ${path} (${source}): ${message}`;

  // Literal first argument: console's format string must not carry
  // user-influenced text (CodeQL js/tainted-format-string).
  console.error('Firestore error:', description, error);

  const dedupeKey = `${code}|${path}`;
  if (reportedToSentry.has(dedupeKey)) return;
  reportedToSentry.add(dedupeKey);

  const report = new Error(description);
  report.name = 'FirestoreError';
  if (error instanceof Error && error.stack) {
    // Keep the original frames — the synthesized error would otherwise point
    // every event at this helper.
    report.stack = error.stack;
  }
  Sentry.captureException(report, {
    tags: { 'firestore.code': code, 'firestore.source': source },
    contexts: { firestore: { code, path, source } },
    // Group per source+code, not per stack frame of this helper; the concrete
    // path stays visible in the message and context.
    fingerprint: ['firestore', code, source],
  });
}

export default logFirestoreError;
